import type { PropsWithChildren, ReactNode } from 'react';
import React, { useContext, useEffect, useMemo } from 'react';
import type { MessageFormatElement } from 'react-intl';
import { FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/button';
import ButtonGroup from '@atlaskit/button/button-group';
import Skeleton from '@atlaskit/skeleton';

import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { EndUserRequestUpgradeButton } from '@confluence/experiment-confluence-enable-end-user-request/entry-points/EndUserRequestUpgradeButton.ts';

import type { ConfluenceEdition } from '../Upgrade/__types__/waitForEditionChangeQuery.ts';
import { LearnMoreButton, type LearnMoreButtonProps } from '../Upgrade/LearnMoreButton.tsx';
import type { UpgradeEditionButtonProps } from '../Upgrade/UpgradeEditionButton.tsx';
import { UpgradeEditionButton } from '../Upgrade/UpgradeEditionButton.tsx';
import { calculateTestId } from '../Upgrade/calculateTestId.ts';
import { useEditionsAnalyticsContext } from '../Upgrade/AnalyticsProvider.tsx';

import type { LinkProps } from './ChangeEditionSectionMessageButtonsWrapper.tsx';
import { ChangeEditionSectionMessageButtonsWrapper } from './ChangeEditionSectionMessageButtonsWrapper.tsx';
import type { EditionSectionMessageProps } from './EditionSectionMessage.tsx';
import { EditionSectionMessage } from './EditionSectionMessage.tsx';

export enum SectionMessageButtonConfig {
	DEFAULT = 'default',
	UPGRADE_ONLY = 'upgradeOnly',
	NONE = 'none',
}

type InProductHelpProps = {
	inProductHelpId?: string;
	inProductHelpTopic?: string;
};

/**
 * @deprecated Please reach out to #cc-ai-growth for details before use. Please read https://hello.atlassian.net/wiki/spaces/CE2/pages/2797298237/Contribution+Consumption+Guidelines+For+Common+Feature+Gate+Upsell+Component
 */
export type ChangeEditionSectionMessageProps = Pick<
	EditionSectionMessageProps,
	| 'appearanceType'
	| 'body'
	| 'footer'
	| 'title'
	| 'headerButton'
	| 'withTasteAndToneStyling'
	| 'buttonGroupAlignment'
> &
	Pick<UpgradeEditionButtonProps, 'onUpgradeButtonClick'> & {
		canShowUpgradeButton?: boolean;
		buttonConfig?: SectionMessageButtonConfig;
		grandfathered?: boolean;
		learnMoreLink?: Partial<LinkProps> & InProductHelpProps;
		onUpFlowOpen?(): void;
		secondaryLink?: LinkProps;
		source: string;
		targetEdition?: ConfluenceEdition;
		upgradeEditionActionSubjectId?: string;
		showRequestUpgradeButton?: boolean;
		// Handles the loading state when checking whether the request upgrade button should be shown.
		isLoadingRequestUpgradeButton?: boolean;
		onRequestUpgradeButtonClick?: () => void;
		// Handles the loading state (spinner) on the actual request upgrade button.
		requestUpgradeButtonIsLoading?: boolean;
	};

/**
 * @deprecated Please reach out to #cc-ai-growth for details before use. Please read https://hello.atlassian.net/wiki/spaces/CE2/pages/2797298237/Contribution+Consumption+Guidelines+For+Common+Feature+Gate+Upsell+Component
 */
export const ChangeEditionSectionMessage = ({
	canShowUpgradeButton = true,
	buttonConfig = SectionMessageButtonConfig.DEFAULT,
	upgradeEditionActionSubjectId = 'freeTryNow',
	grandfathered,
	source,
	onUpFlowOpen,
	secondaryLink,
	targetEdition,
	learnMoreLink,
	footer,
	headerButton,
	showRequestUpgradeButton = false,
	isLoadingRequestUpgradeButton = false,
	onRequestUpgradeButtonClick,
	requestUpgradeButtonIsLoading = false,
	...props
}: ChangeEditionSectionMessageProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useEditionsAnalyticsContext();
	const { isSiteAdmin } = useContext(SPAViewContext);

	const CTAAnalyticsAttributes = useMemo(() => {
		return {
			isSiteAdmin,
			isGrandfathered: Boolean(grandfathered),
		};
	}, [grandfathered, isSiteAdmin]);

	const generateCTAAnalyticsProps = (actionSubjectId: string) => ({
		actionSubjectId,
		source,
		attributes: CTAAnalyticsAttributes,
	});

	const triggerLinkClickAnalytics = (
		clickEventActionSubjectId: string,
		linkMessage: string | MessageFormatElement[] | undefined,
	) => {
		if (createAnalyticsEvent) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'link',
					actionSubjectId: clickEventActionSubjectId,
					source,
					attributes: {
						...CTAAnalyticsAttributes,
						linkMessage,
					},
				},
			}).fire();
		}
	};

	const handleSecondaryLinkClick = () => {
		const link = secondaryLink;
		if (link) {
			triggerLinkClickAnalytics(link.actionSubjectId, link.messageDescriptor.defaultMessage);
			if (typeof link.onClick === 'function') {
				link.onClick();
			}
		}
	};

	useEffect(() => {
		if (createAnalyticsEvent) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'changeEditionGatewayScreen',
					attributes: {
						...CTAAnalyticsAttributes,
						source,
					},
				},
			}).fire();
		}
	}, [createAnalyticsEvent, CTAAnalyticsAttributes, source]);

	const renderButtons = () => {
		if (isLoadingRequestUpgradeButton) {
			return (
				<Skeleton
					testId="loading-end-user-request-upgrade-button-skeleton"
					width="200px"
					height="32px"
				/>
			);
		}

		return (
			<ChangeEditionSectionMessageButtonsWrapper
				isSiteAdmin={isSiteAdmin}
				canShowUpgradeButton={canShowUpgradeButton}
				shouldShowLearnMore={!secondaryLink}
				showRequestUpgradeButton={showRequestUpgradeButton}
			>
				{({ showUpgradeTryNowButton, showLearnMore, learnMoreIsPrimaryCTA }) => {
					//
					// primary
					//
					let primary: ReactNode;

					const upgradeEditionButton = showUpgradeTryNowButton ? (
						<UpgradeEditionButton
							analyticsProps={generateCTAAnalyticsProps(upgradeEditionActionSubjectId)}
							onUpFlowOpen={onUpFlowOpen}
							targetEdition={targetEdition}
							{...props}
						/>
					) : null;
					if (upgradeEditionButton) {
						primary = upgradeEditionButton;
					} else if (showRequestUpgradeButton) {
						primary = (
							<EndUserRequestUpgradeButton
								source={source}
								onRequestUpgradeButtonClick={onRequestUpgradeButtonClick}
								requestUpgradeButtonIsLoading={requestUpgradeButtonIsLoading}
							/>
						);
					}

					//
					// secondaries
					//
					let secondaries: ReactNode[] | undefined;

					if (buttonConfig !== SectionMessageButtonConfig.UPGRADE_ONLY || !isSiteAdmin) {
						// If the 'Try it free/now' button is to be shown, but it is not the
						// primary CTA, then make sure its appearance is appropriate for a
						// secondary:
						if (upgradeEditionButton && primary !== upgradeEditionButton) {
							secondaries = [
								React.cloneElement(upgradeEditionButton, {
									appearance: 'link',
								}),
							];
						} else {
							secondaries = getSecondaries(showLearnMore, learnMoreIsPrimaryCTA);
						}
					}

					return (
						<>
							{primary}
							{secondaries}
						</>
					);
				}}
			</ChangeEditionSectionMessageButtonsWrapper>
		);
	};

	const getSecondaries = (showLearnMore: boolean, learnMoreIsPrimaryCTA: boolean) => {
		const withIcon = Boolean(learnMoreLink?.icon);
		const learnMoreAppearance =
			withIcon || learnMoreIsPrimaryCTA ? learnMoreLink?.appearance || 'default' : 'link';

		const learnMoreButton = showLearnMore
			? renderLearnMoreLink({
					appearance: learnMoreAppearance,
				})
			: null;

		const secondaryLinkComponent = secondaryLink ? (
			<Button
				key="secondLink"
				appearance={secondaryLink.appearance || 'default'}
				href={secondaryLink.href ? secondaryLink.href : undefined}
				id="secondaryLink"
				spacing="default"
				target={secondaryLink.target || '_blank'}
				onClick={handleSecondaryLinkClick}
				testId={calculateTestId({
					source,
					actionSubjectId: secondaryLink.actionSubjectId,
				})}
			>
				<FormattedMessage {...secondaryLink.messageDescriptor} />
			</Button>
		) : null;

		return secondaryLink?.appearance === 'primary' && learnMoreAppearance !== 'primary'
			? [secondaryLinkComponent, learnMoreButton]
			: [learnMoreButton, secondaryLinkComponent];
	};

	const renderLearnMoreLink = ({
		children,
		...learnMoreProps
	}: PropsWithChildren<Partial<Omit<LearnMoreButtonProps, 'onClick'>>>) => {
		return (
			<LearnMoreButton
				key="learnMore"
				analyticsProps={generateCTAAnalyticsProps(
					learnMoreLink?.actionSubjectId || 'freeLearnMore',
				)}
				appearance={learnMoreLink?.appearance || 'default'}
				onUpFlowOpen={onUpFlowOpen}
				targetEdition={targetEdition}
				inProductHelpId={learnMoreLink?.inProductHelpId}
				inProductHelpTopic={learnMoreLink?.inProductHelpTopic}
				href={learnMoreLink?.href}
				icon={learnMoreLink?.icon}
				ariaLabel={learnMoreLink?.ariaLabel}
				{...learnMoreProps}
				onClick={learnMoreLink?.onClick}
			>
				{children !== undefined ? (
					children
				) : learnMoreLink?.messageDescriptor ? (
					<FormattedMessage {...learnMoreLink.messageDescriptor} />
				) : undefined}
			</LearnMoreButton>
		);
	};

	return (
		<EditionSectionMessage
			actions={
				buttonConfig !== SectionMessageButtonConfig.NONE ? (
					<ButtonGroup>{renderButtons()}</ButtonGroup>
				) : undefined
			}
			edition={targetEdition}
			footer={isSiteAdmin && footer ? <>{footer}</> : null}
			headerButton={<ButtonGroup>{headerButton}</ButtonGroup>}
			{...props}
		/>
	);
};
